import styled from '@emotion/styled';
import { Button, Card } from '@mui/material';
import FixedWidth from 'components/FixedWidth';
import { A11y, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import arrleft from './arrow-left.svg';
import arrright from './arrow-right.svg';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const StyledSection = styled.section`
  padding: 4rem 0 2rem 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  .MuiCard-root {
    padding: 3rem 16rem;
    box-shadow: unset;
  }
  h2,
  h4 {
    color: #6f6259;
    padding: 0;
  }
  .swiper::before {
    box-shadow: inset 1rem 1rem 1rem 1rem #000;
    position: absolute;
    height: 100%;
    width: 4.25rem;
  }
  .swiper-button-next,
  .swiper-button-prev {
    background-position: center;
    background-size: 37.75px;
    background-repeat: no-repeat;
    padding: 0 3rem;
  }

  .swiper-button-next {
    right: 1rem;
    box-shadow: inset -1rem 0 7px 0px #fff;
    background-image: url(${arrright});
  }

  .swiper-button-prev {
    left: 0;
    box-shadow: inset 1rem 0 7px 0px #fff;
    background-image: url(${arrleft});
  }

  .swiper-button-next::after,
  .swiper-button-prev::after {
    content: '';
  }

  .swiper-pagination-bullet-active {
    background: #6f6259;
  }
  @media (max-width: 756px) {
    .swiper {
      width: 346px;
    }
    .swiper-slide {
      width: 100% !important;
    }
    .MuiCard-root {
      padding: 0 0 3rem 0;
      box-shadow: unset;
    }
    .swiper-button-prev {
      left: -1.8rem;
      top: var(--swiper-navigation-top-offset, 93%);
      z-index: 1;
    }
    .swiper-button-next {
      right: -1.8rem;
      top: var(--swiper-navigation-top-offset, 93%);
      z-index: 1;
    }
    .swiper-pagination {
      z-index: 0;
    }
  }
  @media (min-width: 756px) and (max-width: 1024px) {
    .swiper {
      width: 600px;
    }
    .swiper-slide {
      width: 100% !important;
    }
    .MuiCard-root {
      padding: 0;
      box-shadow: unset;
    }
    .swiper-button-prev {
      left: -1.8rem;
      top: var(--swiper-navigation-top-offset, 93%);
      z-index: 1;
    }
    .swiper-button-next {
      right: -1.8rem;
      top: var(--swiper-navigation-top-offset, 93%);
      z-index: 1;
    }
  }
`;

export default function Partners(): JSX.Element {
  return (
    <StyledSection>
      <h2>What our customers and partners are saying</h2>
      <FixedWidth>
        <Swiper
          // install Swiper modules
          modules={[Navigation, Pagination, A11y]}
          spaceBetween={10}
          slidesPerView={1}
          freeMode={true}
          navigation
          centeredSlides={true}
          pagination={{ clickable: true }}
        >
          <SwiperSlide>
            <Card>
              <h3>
                “Manon and the Inkwell team break what is usually a tradeoff
                between flexibility vs. compensation. When I was leaving an
                almost five year post as an Executive at a Fortune 500 company,
                they found my next dream job. A role that allowed me to use the
                breadth of my experience, bring my purpose to life, have more
                flexibility and maintain my compensation expectations.”
              </h3>
              <h4>Mo Mullen, Founder, CEO at Momentum</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “As a father of two young daughters and a spouse who works
                remotely, I love that Inkwell is transforming the way executive
                recruiting and referral systems work for the benefit of women
                and diverse candidates.”
              </h3>
              <h4>Thomas Vu</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “Inkwell is a valuable resource for companies and executives
                alike: its platform taps into an incredible group of highly
                sought after talent while building trust and community.”
              </h3>
              <h4>Kendra Ragatz</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “40% of high-growth private companies didn't have a woman on
                their board in 2021. 78% didn’t have any women of color. I
                recommend Inkwell because they’re actively placing more women
                into the corporate landscape at the highest levels. I believe
                this will be one of the largest drivers of value creation for
                high growth companies over the next decade.”
              </h3>
              <h4>Heather Hartnett CEO, General Partner, Human Ventures VC</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “Inkwell is addressing a noticeable gap in the experienced hire
                landscape. The talent pool it has attracted and captured in a
                short amount of time shows how critical the existence of this
                Company is.”
              </h3>
              <h4>Shannon Sweeney, Managing Director at J.P. Morgan</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “I've loved working with Inkwell to help dynamic and talented
                people in my network connect to great career opportunities. The
                icing on the cake is the referral bonus that Inkwell pays me
                when I refer a terrific candidate!”
              </h3>
              <h4>Gina Clark des Cognets</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “I was looking for a more flexible position to allow me to
                fulfill personal goals while feeling like I was contributing my
                strengths to a great organization. Joining the place Inkwell
                connected me with has allowed me to do both, even as I was hired
                to work remotely 6 weeks prior to the pandemic!”
              </h3>
              <h4>Katie Slater, Sageworth</h4>
            </Card>
          </SwiperSlide>
          <SwiperSlide>
            <Card>
              <h3>
                “Inkwell is a game changer for how my businesses advance! The
                caliber of the candidates they are able to deliver and the speed
                at which they do is if unprecedented. This is a service that
                finally keeps up with the pace at which businesses evolve
                today.”
              </h3>
              <h4>Jody Levy | Global Director + CEO Summit Group Endeavors</h4>
            </Card>
          </SwiperSlide>
        </Swiper>
        <br />
        <Button
          href="/for-businesses/new-business"
          color="primary"
          variant="contained"
        >
          Start hiring
        </Button>
      </FixedWidth>
    </StyledSection>
  );
}
